import styled from '@emotion/styled';
import {media, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';

import type {WithChildren} from 'types/global';

/**
 * The `ComponentProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface ComponentProps {
    /**
     * Optional. A boolean flag that, when true, removes the padding at the bottom of the component,
     * allowing for tighter spacing with subsequent content. Useful for adjusting layout spacing in designs.
     *
     * @default false
     */
    hasNoPaddingBottom?: boolean;
    /**
     * Optional. A boolean flag that, when true, removes the padding at the top of the component,
     * enabling closer placement to preceding content, which can improve layout efficiency and visual flow.
     *
     * @default false
     */
    hasNoPaddingTop?: boolean;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Section'
     */
    testId?: string;
}

/**
 * Renders a `Section` component with customizable background color and padding options.
 * This component serves as a generic container for grouping related UI elements, offering flexibility
 * in visual styling through background color customization and the ability to remove padding from the top
 * or bottom of the section. The `Section` component is highly versatile and can wrap any children, making it
 * useful for organizing different parts of a webpage or application.
 * The component supports theme reactivity, allowing it to adjust its appearance based on the active theme when
 * the `$reactsToTheme` prop is set to true. Additionally, it provides unique `testId` support for precise targeting
 * during testing.
 *
 * @param props                    The component props.
 * @param props.children           The content to be rendered within the section, enabling composition of diverse UI elements.
 * @param props.hasNoPaddingBottom Optional flag to remove padding at the bottom of the section, useful for visual compactness.
 * @param props.hasNoPaddingTop    Optional flag to remove padding at the top of the section, allowing for closer alignment with preceding elements.
 * @param props.testId             A unique identifier for testing purposes, ensuring the component can be precisely targeted within test suites.
 * @returns A `Wrapper` component representing a section with the specified styling and content.
 *
 * @example
 * ```tsx
 * <Section bgColor="blackBoxBg" hasNoPaddingBottom={true} testId="hero-section">
 *   <HeroContent />
 * </Section>
 * ```
 */
const Section = observer(({
    children,
    hasNoPaddingBottom = false,
    hasNoPaddingTop = false,
    testId = 'Section'
}: WithChildren<ComponentProps>) => (
    <Wrapper
        $hasNoPaddingBottom={hasNoPaddingBottom}
        $hasNoPaddingTop={hasNoPaddingTop}
        data-cy={testId}
    >
        {children}
    </Wrapper>
));

Section.displayName = 'Section';

export {Section};

interface WrapperProps {
    $hasNoPaddingBottom?: boolean;
    $hasNoPaddingTop?: boolean;
}

const Wrapper = styled.section<WrapperProps>`
    background-color: ${({theme}) => theme.colors.bgPrimary};
    contain: paint;
    padding-block-end: ${({$hasNoPaddingBottom}) => ($hasNoPaddingBottom ? null : spacing(10))};
    padding-block-start: ${({$hasNoPaddingTop}) => ($hasNoPaddingTop ? null : spacing(10))};
    transition: background-color 0.2s ease-in-out;
    will-change: background-color;

    ${media('md')} {
        padding-block-end: ${({$hasNoPaddingBottom}) => ($hasNoPaddingBottom ? null : spacing(20))};
        padding-block-start: ${({$hasNoPaddingTop}) => ($hasNoPaddingTop ? null : spacing(20))};
    }
`;